// src/hooks/useTranslate.js
import { useState, useEffect, useRef } from "react";

// ─── MyMemory API — gratis, tanpa API key, tanpa install apapun ───────────────
// Limit: ~1000 kata/hari untuk IP yang sama (cukup untuk project kecil-menengah)
// Docs : https://mymemory.translated.net/doc/spec.php
const MYMEMORY_URL = "https://api.mymemory.translated.net/get";

// Cache in-memory: hindari request ulang untuk teks yang sudah pernah diterjemahkan
const cache = {};

/**
 * Terjemahkan satu string via MyMemory.
 * @param {string} text   - teks sumber (Bahasa Indonesia)
 * @param {string} target - kode bahasa tujuan, mis. "en"
 * @returns {Promise<string>} teks terjemahan, atau teks asli jika gagal
 */
async function translateText(text, target) {
  if (!text || target === "id") return text;

  const cacheKey = `${target}::${text}`;
  if (cache[cacheKey]) return cache[cacheKey];

  try {
    // MyMemory pakai GET request dengan query param
    const params = new URLSearchParams({
      q:        text,
      langpair: `id|${target}`,
      // de: "emailkamu@gmail.com", // ← opsional: daftarkan email untuk limit lebih tinggi (10.000 kata/hari)
    });

    const res = await fetch(`${MYMEMORY_URL}?${params}`);
    if (!res.ok) throw new Error("MyMemory error");

    const data = await res.json();

    // responseStatus 200 = sukses
    const result =
      data.responseStatus === 200
        ? data.responseData.translatedText
        : text; // fallback ke teks asli jika gagal

    cache[cacheKey] = result;
    return result;
  } catch {
    return text; // fallback jika network error
  }
}

/**
 * Hook untuk menerjemahkan array objek dari API secara otomatis.
 *
 * @param {Array}    items    - array data dari API (mis. teamMembers)
 * @param {string}   language - "EN" atau "ID" dari LanguageContext
 * @param {string[]} fields   - field yang ingin diterjemahkan, mis. ["role","deskripsi"]
 * @returns {{ translated: Array, loading: boolean }}
 *
 * Contoh pemakaian:
 *   const { translated, loading } = useTranslateItems(rawMembers, language, ["role","deskripsi"]);
 */
export function useTranslateItems(items, language, fields) {
  const [translated, setTranslated] = useState(items);
  const [loading, setLoading]       = useState(false);
  const sessionCache = useRef({});   // cache per-session agar tidak request ulang

  useEffect(() => {
    // Bahasa ID → kembalikan data asli tanpa request apapun
    if (language === "ID" || !items.length) {
      setTranslated(items);
      return;
    }

    const target   = language.toLowerCase(); // "EN" → "en"
    const cacheKey = `${target}::${items.map((i) => i.id).join(",")}`;

    // Sudah pernah diterjemahkan sesi ini? Pakai cache langsung
    if (sessionCache.current[cacheKey]) {
      setTranslated(sessionCache.current[cacheKey]);
      return;
    }

    setLoading(true);

    const run = async () => {
      const results = await Promise.all(
        items.map(async (item) => {
          // Terjemahkan semua field sekaligus secara paralel
          const translatedValues = await Promise.all(
            fields.map((f) => translateText(item[f] ?? "", target))
          );

          // Gabungkan hasil terjemahan ke dalam objek item
          const patch = {};
          fields.forEach((f, i) => { patch[f] = translatedValues[i]; });
          return { ...item, ...patch };
        })
      );

      sessionCache.current[cacheKey] = results;
      setTranslated(results);
      setLoading(false);
    };

    run();
  }, [items, language]);  // re-run hanya jika items atau language berubah

  return { translated, loading };
}